import React, { useState, useRef, useEffect } from 'react';
import { isMobileDevice } from '../../../utils/deviceDetection';

const formatNumber = (num) => {
  if (num == null || isNaN(num)) return "0.00";
  return Number(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

const slippagePresets = [
  { value: 0.1, label: "0.1%" },
  { value: 0.5, label: "0.5%" },
  { value: 1, label: "1%" },
  { value: 2, label: "2%" },
];

export const EstimatedSlippage = ({
  expectedPrice,
  actualPrice,
  slippage,
  setSlippage,
  isAutoSlippage,
  setIsAutoSlippage,
  onSlippageChange,
  openingFee,
  closingFee,
  isLoadingFees,
  dailyBorrowingRate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const slippageInputRef = useRef(null);
  const prevIsAutoSlippageRef = useRef(true);

  // Auto-scroll to slippage input when switching from auto to manual on mobile
  useEffect(() => {
    const wasAuto = prevIsAutoSlippageRef.current;
    const isNowManual = !isAutoSlippage;

    if (wasAuto && isNowManual && isMobileDevice() && slippageInputRef.current) {
      setTimeout(() => {
        slippageInputRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        setTimeout(() => {
          slippageInputRef.current?.focus();
        }, 300);
      }, 100);
    }

    prevIsAutoSlippageRef.current = isAutoSlippage;
  }, [isAutoSlippage]);

  const calculateEstimatedSlippage = () => {
    if (!expectedPrice || !actualPrice || expectedPrice <= 0) {
      return { estimated: 0, max: slippage || 0 };
    }

    const priceDifference = Math.abs(expectedPrice - actualPrice);
    const priceThreshold = expectedPrice * 0.01;

    if (priceDifference > priceThreshold) {
      return { estimated: 0, max: slippage || 0 };
    }

    const estimatedSlippage = ((expectedPrice - actualPrice) / expectedPrice) * 100;
    return {
      estimated: Math.abs(estimatedSlippage),
      max: slippage || 0,
    };
  };

  const { estimated, max } = calculateEstimatedSlippage();

  const handleAutoClick = () => {
    if (isAutoSlippage) {
      // Currently auto -> switch to manual and open dropdown to show presets
      setIsAutoSlippage(false);
      setIsOpen(true);
    } else {
      // Currently manual -> switch back to auto
      setIsAutoSlippage(true);
      setSlippage(0.5);
    }
  };

  const handlePresetClick = (value) => {
    setIsAutoSlippage(false);
    onSlippageChange(value);
  };

  const handleInputChange = (e) => {
    setIsAutoSlippage(false);
    const val = e.target.value;
    if (val === "" || val === ".") {
      setSlippage(val === "" ? 0 : val);
      return;
    }

    if (/^(\d*\.?\d{0,2})$/.test(val)) {
      const numVal = parseFloat(val);
      if (!isNaN(numVal)) {
        if (numVal > 100) {
          setSlippage(100);
        } else if (numVal < 0) {
          setSlippage(0);
        } else {
          setSlippage(val);
        }
      } else {
        setSlippage(val);
      }
      if (val !== "." && val.slice(-1) !== ".") {
        onSlippageChange(val);
      }
    }
  };

  const handleInputBlur = () => {
    if (slippage === "." || slippage === "") {
      setSlippage(0.5);
    } else {
      const numVal = parseFloat(slippage);
      if (!isNaN(numVal)) {
        setSlippage(Math.round(numVal * 100) / 100);
      } else {
        setSlippage(0.5);
      }
    }
  };

  return (
    <div
      style={{ width: "100%", padding: "14px" }}
      className="ce-panel flex flex-col"
    >
      {/* Slippage Header - always visible */}
      <div className="flex justify-between items-center">
        <span
          style={{
            fontFamily: "General Sans",
            fontWeight: 500,
            fontSize: "16px",
            color: "var(--ink)",
          }}
        >
          Slippage & Fees
        </span>
        <div className="flex items-center gap-2">
          <span
            style={{
              fontFamily: "General Sans",
              fontWeight: 500,
              fontSize: "14px",
              color: "var(--up)",
            }}
          >
            Est: {formatNumber(estimated)}%
          </span>
          <span
            style={{
              fontFamily: "General Sans",
              fontWeight: 500,
              fontSize: "14px",
              color: "var(--ink)",
            }}
          >
            /
          </span>
          <span
            style={{
              fontFamily: "General Sans",
              fontWeight: 500,
              fontSize: "14px",
              color: "var(--down)",
            }}
          >
            Max: {isAutoSlippage ? "Auto" : `${formatNumber(max)}%`}
          </span>
          <button
            type="button"
            className="ce-btn ce-btn-surface"
            style={{ width: "32px", height: "32px", padding: 0, flex: "0 0 auto" }}
            onClick={() => setIsOpen(!isOpen)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transition: "transform 0.2s ease",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="flex flex-col" style={{ gap: "16px", marginTop: "16px" }}>
          {/* Opening Fee and Closing Fee Boxes */}
          <div className="flex gap-3">
            {/* Opening Fee Box */}
            <div
              style={{ flex: 1, minHeight: "71px", padding: "12px" }}
              className="ce-well-sm flex flex-col justify-center"
            >
              <span
                style={{
                  fontFamily: "General Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "var(--ink-2)",
                }}
              >
                Opening Fee
              </span>
              <div className="flex items-baseline gap-1">
                <span
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "var(--ink)",
                  }}
                >
                  ${isLoadingFees ? "0.10" : formatNumber(openingFee.feeAmount)}
                </span>
                <span
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "var(--ink-2)",
                  }}
                >
                  ({isLoadingFees ? "0.10" : openingFee.feePercentage.toFixed(2)}%)
                </span>
              </div>
            </div>

            {/* Closing Fee Box */}
            <div
              style={{ flex: 1, minHeight: "71px", padding: "12px" }}
              className="ce-well-sm flex flex-col justify-center"
            >
              <span
                style={{
                  fontFamily: "General Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "var(--ink-2)",
                }}
              >
                Closing fee
              </span>
              <div className="flex items-baseline gap-1">
                <span
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "var(--ink)",
                  }}
                >
                  ${isLoadingFees ? "0.10" : formatNumber(closingFee.totalFee)}
                </span>
                <span
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "var(--ink-2)",
                  }}
                >
                  ({isLoadingFees ? "0.10" : closingFee.feePercentage.toFixed(2)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Daily Borrowing Rate Box */}
          {dailyBorrowingRate && (
            <div
              style={{ width: "100%", minHeight: "71px", padding: "12px" }}
              className="ce-well-sm flex flex-col justify-center"
            >
              <span
                style={{
                  fontFamily: "General Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "var(--ink-2)",
                }}
              >
                Daily Borrowing Rate
              </span>
              {dailyBorrowingRate.paysSide ? (
                <div className="flex items-baseline gap-1">
                  <span
                    style={{
                      fontFamily: "General Sans",
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "var(--ink)",
                    }}
                  >
                    ${isLoadingFees ? "0.00" : formatNumber(dailyBorrowingRate.dailyCost)}
                  </span>
                  <span
                    style={{
                      fontFamily: "General Sans",
                      fontWeight: 400,
                      fontSize: "12px",
                      color: "var(--ink-2)",
                    }}
                  >
                    ({isLoadingFees ? "0.00" : dailyBorrowingRate.dailyRatePct.toFixed(4)}%/day)
                  </span>
                </div>
              ) : (
                <div className="flex items-baseline gap-1">
                  <span
                    style={{
                      fontFamily: "General Sans",
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "var(--up)",
                    }}
                  >
                    $0.00
                  </span>
                  <span
                    style={{
                      fontFamily: "General Sans",
                      fontWeight: 400,
                      fontSize: "11px",
                      color: "var(--ink-2)",
                    }}
                  >
                    (market rate: {dailyBorrowingRate.marketDailyRatePct.toFixed(4)}%/day)
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Slippage & Fees Info */}
          <div className="flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M8 7V11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle cx="8" cy="5" r="0.5" fill="currentColor" />
            </svg>
            <span
              style={{
                fontFamily: "General Sans",
                fontWeight: 400,
                fontSize: "12px",
                color: "var(--ink-2)",
              }}
            >
              Slippage & Fees
            </span>
          </div>

          {/* Slippage Tolerance */}
          <div className="flex justify-between items-center">
            <span
              style={{
                fontFamily: "General Sans",
                fontWeight: 500,
                fontSize: "14px",
                color: "var(--ink)",
              }}
            >
              Slippage Tolerance
            </span>
            <button
              onClick={handleAutoClick}
              type="button"
              style={{
                fontFamily: "General Sans",
                fontWeight: 500,
                fontSize: "14px",
                height: "var(--h-control-sm)",
                padding: "0 16px",
              }}
              className={`ce-btn ${isAutoSlippage ? "ce-btn-accent" : "ce-btn-surface"}`}
            >
              Auto
            </button>
          </div>

          {/* Manual slippage controls - shown when not auto */}
          {!isAutoSlippage && (
            <>
              {/* Percentage Buttons */}
              <div className="flex gap-2">
                {slippagePresets.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetClick(preset.value)}
                    style={{
                      fontFamily: "General Sans",
                      fontWeight: 500,
                      fontSize: "14px",
                      flex: 1,
                      height: "var(--h-control)",
                      padding: "0 12px",
                    }}
                    className={`ce-btn ${
                      Number(slippage) === preset.value ? "ce-btn-accent" : "ce-btn-surface"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Slippage Input */}
              <div
                className="ce-field relative"
                style={{
                  background: "var(--surface-2)",
                  borderRadius: "var(--r-inner)",
                  boxShadow: "var(--inset-sm)",
                }}
              >
                <input
                  ref={slippageInputRef}
                  type="text"
                  value={slippage === 0 ? "" : slippage}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="0.5"
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    width: "100%",
                    border: 0,
                    borderRadius: "inherit",
                    padding: "12px 40px 12px 16px",
                    background: "transparent",
                    boxShadow: "none",
                    color: "var(--ink)",
                  }}
                  className="ce-bare outline-none"
                />
                <span
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "var(--ink-2)",
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  %
                </span>
              </div>

              {slippage > 5 && (
                <div
                  style={{
                    fontFamily: "General Sans",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "var(--on-tint-warn)",
                    backgroundColor: "var(--tint-warn)",
                    borderRadius: "var(--r-control)",
                    padding: "8px 12px",
                  }}
                >
                  High slippage tolerance may result in unfavorable trade execution
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
